import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	AuthErrorResponse,
	ChangePasswordRequest,
	ErrorResponse,
	LoginRequest,
	LoginResponse,
	RequirePasswordResetRequest,
	SpoofUserResponse,
	StopSpoofResponse,
	UpdatePasswordRequest,
} from '@/api/auth/types';
import {
	changePassword,
	getAuthenticatedUser,
	login,
	logout,
	sendPasswordResetEmail,
	spoofUser,
	stopSpoof,
	updatePassword,
} from '@/api/auth';
import {
	LOGIN_ROUTE_INDICATOR,
	getAuthDataFromLoginResponse,
	getAuthUserFromResponse,
	saveAuthData,
} from '@/shared/utils/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocationState } from '@/router/guards/types';
import { AxiosError } from 'axios';
import useToastMessage from '@/shared/context/toast-message-context';
import { CreateMutationFnParams, UseMutationQueryParams } from './types';

const authKeys = {
	me: () => ['me'],
};

export const useLogin = (mutationQueryParams?: UseMutationQueryParams) => {
	const location = useLocation() as { state?: LocationState };
	const navigate = useNavigate();

	return useMutation({
		mutationFn: ({ request }: CreateMutationFnParams<LoginRequest>) => login(request),
		...mutationQueryParams,
		onSuccess: async (loginResponse: LoginResponse) => {
			const authData = getAuthDataFromLoginResponse(loginResponse);
			saveAuthData(authData);
			navigate(location.state?.from ?? '/', { state: { from: LOGIN_ROUTE_INDICATOR } });
			await mutationQueryParams?.onSuccess?.();
		},
		onError: async (error: AxiosError<AuthErrorResponse>) => {
			switch (error.response?.data?.statusCode) {
				case 400:
				case 401: {
					await mutationQueryParams?.onError?.();
					break;
				}
				default:
					console.error(error);
			}
		},
	});
};

export const useSendPasswordResetEmail = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: ({ request }: CreateMutationFnParams<RequirePasswordResetRequest>) =>
			sendPasswordResetEmail(request),
		...mutationQueryParams,
		onError: async (error: AxiosError<AuthErrorResponse>) => {
			if (error.response?.data?.statusCode === 400) {
				await mutationQueryParams?.onError?.();
			}
		},
	});

export const useUpdatePassword = (mutationQueryParams?: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();

	return useMutation({
		mutationFn: ({ request }: CreateMutationFnParams<UpdatePasswordRequest>) =>
			updatePassword(request),
		...mutationQueryParams,
		onSuccess: async () => {
			dispatch({
				title: 'Password successfully updated!',
				content: 'You can now use your new password to log in into your account.',
				type: 'success',
			});
			await mutationQueryParams?.onSuccess?.();
		},
		onError: async (error: AxiosError<AuthErrorResponse>) => {
			const { statusCode, message } = error.response?.data || {};
			if (statusCode === 401 || statusCode === 400) {
				dispatch({
					content: message,
					title: 'Error while creating password',
					type: 'error',
				});
			}
			await mutationQueryParams?.onError?.();
		},
	});
};

export const useLogout = (mutationQuery?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: () => logout(),
		...mutationQuery,
	});

export const useAuthenticatedUser = () => {
	const queryKey = authKeys.me();
	const query = useQuery({
		queryKey,
		queryFn: () => getAuthenticatedUser(),
		select: (response) => getAuthUserFromResponse(response),
		refetchOnWindowFocus: false,
	});

	return { queryKey, query };
};

export const useChangePassword = (data: ChangePasswordRequest) => {
	const mutation = useMutation<unknown, AxiosError<ErrorResponse>>({
		mutationFn: () => changePassword(data),
	});

	return mutation;
};

export const useSpoofUser = (mutationQueryParams?: UseMutationQueryParams) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { dispatch } = useToastMessage();

	return useMutation({
		mutationFn: (userId: string) => spoofUser(userId),
		...mutationQueryParams,
		onSuccess: async (response: SpoofUserResponse) => {
			const authData = getAuthDataFromLoginResponse(response);
			saveAuthData(authData);
			dispatch({
				title: 'Spoofing Active',
				content: `Now viewing as ${response.spoofedUser.firstName} ${response.spoofedUser.lastName}`,
				type: 'info',
			});
			// Invalidate the current user query to refetch with new token
			await queryClient.invalidateQueries({ queryKey: authKeys.me() });
			navigate('/', { replace: true });
			await mutationQueryParams?.onSuccess?.();
		},
		onError: async (error: AxiosError<AuthErrorResponse>) => {
			dispatch({
				title: 'Spoof Failed',
				content: error.response?.data?.message || 'Could not spoof user',
				type: 'error',
			});
			await mutationQueryParams?.onError?.();
		},
	});
};

export const useStopSpoof = (mutationQueryParams?: UseMutationQueryParams) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { dispatch } = useToastMessage();

	return useMutation({
		mutationFn: () => stopSpoof(),
		...mutationQueryParams,
		onSuccess: async (response: StopSpoofResponse) => {
			const authData = getAuthDataFromLoginResponse(response);
			saveAuthData(authData);
			dispatch({
				title: 'Spoofing Stopped',
				content: 'Returned to your admin account',
				type: 'success',
			});
			// Invalidate the current user query to refetch with new token
			await queryClient.invalidateQueries({ queryKey: authKeys.me() });
			navigate('/', { replace: true });
			await mutationQueryParams?.onSuccess?.();
		},
		onError: async (error: AxiosError<AuthErrorResponse>) => {
			dispatch({
				title: 'Error',
				content: error.response?.data?.message || 'Could not stop spoofing',
				type: 'error',
			});
			await mutationQueryParams?.onError?.();
		},
	});
};